import { DocumentVersion } from '../types/documentVersion.types';
import { VersionStatusInfo, VersionStats } from '../types/documentVersion.types';

// Enhanced utility functions with REAL DATA SOURCES
export const formatVersionLabel = (versionNumber?: string | number | null): string => {
  const raw = String(versionNumber ?? '').trim();
  if (!raw) return 'Version';
  return raw.toLowerCase().startsWith('v') ? raw : `v${raw}`;
};

export const getVersionDisplayName = (version: DocumentVersion): string => {
  const versionLabel = formatVersionLabel(version.version_number);

  // Enhancement 1: Clean user-friendly titles from actual data
  if (version.changes_description && version.changes_description.trim()) {
    return `Document ${versionLabel} - ${version.changes_description}`;
  }
  
  // Generate friendly name from original_name if no description
  if (version.original_name) {
    // Extract meaningful part from filename (remove UUID/timestamp parts)
    const cleanName = version.original_name
      .replace(/lexical-[a-f0-9-]+/g, '') // Remove lexical UUID
      .replace(/-v\d+\.\d+-\d+-[a-f0-9]+\.json/g, '') // Remove version suffix
      .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
      .replace(/-+/g, ' ') // Replace dashes with spaces
      .trim();
    
    if (cleanName) {
      return `Document ${versionLabel} - ${cleanName}`;
    }
  }
  
  return `Document ${versionLabel}`;
};

export const getUserDisplayName = (
  userId: string, 
  contacts: any[], 
  currentUserInfo: any, 
  keycloakTokenParsed: any
): string => {
  // Enhancement 2: DUAL USER SYSTEM AWARE - Handles Keycloak ID vs DMS ID
  if (!userId || userId.length < 8) return 'Unknown User';
  
  const tokenUserId = keycloakTokenParsed?.sub;        // Keycloak user ID
  const tokenUserName = keycloakTokenParsed?.name;     // "David Duncan"
  const tokenRoles = keycloakTokenParsed?.realm_access?.roles || [];
  
  console.log('🔍 [getUserDisplayName] Dual User System Debug:', {
    userId,              // DMS User ID: f7afbdd-60e4-4201-9456-5fc7e65de322
    tokenUserId,         // Keycloak ID: 4226fd8a-3979-45c1-a678-bbcb47007312  
    tokenUserName,       // David Duncan
    isDualUserSystem: userId !== tokenUserId
  });
  
  // EXACT MATCH: Direct Keycloak ID match (legacy documents)
  if (tokenUserId && userId === tokenUserId && tokenUserName) {
    let roleDisplay = '';
    if (tokenRoles.includes('attorney')) roleDisplay = '(Attorney)';
    else if (tokenRoles.includes('admin')) roleDisplay = '(Admin)';
    else if (tokenRoles.includes('assistant')) roleDisplay = '(Assistant)';
    else if (tokenRoles.includes('billing')) roleDisplay = '(Billing)';
    else roleDisplay = '(User)';
    
    console.log('✅ [getUserDisplayName] KEYCLOAK ID match:', `${tokenUserName} ${roleDisplay}`);
    return `${tokenUserName} ${roleDisplay}`;
  }
  
  // CURRENT USER HINT: Check if this is likely the current user's DMS ID
  // Based on context: recent documents in active session are likely yours
  if (tokenUserName && userId.length === 36 && userId.includes('-')) {
    // This looks like a DMS User ID format, and we're in an active session
    // High probability this is the current user's DMS operations
    
    let roleDisplay = '';
    if (tokenRoles.includes('attorney')) roleDisplay = '(Attorney)';
    else if (tokenRoles.includes('admin')) roleDisplay = '(Admin)';
    else if (tokenRoles.includes('assistant')) roleDisplay = '(Assistant)';
    else if (tokenRoles.includes('billing')) roleDisplay = '(Billing)';
    else roleDisplay = '(User)';
    
    console.log('✅ [getUserDisplayName] LIKELY CURRENT USER (DMS ID):', `${tokenUserName} ${roleDisplay}`);
    return `${tokenUserName} ${roleDisplay}`;
  }
  
  // LEGACY: Try currentUserInfo approach
  if (currentUserInfo && userId === currentUserInfo.id && currentUserInfo.fullName) {
    let roleDisplay = '';
    if (tokenRoles.includes('attorney')) roleDisplay = '(Attorney)';
    else if (tokenRoles.includes('admin')) roleDisplay = '(Admin)';
    else if (tokenRoles.includes('assistant')) roleDisplay = '(Assistant)';
    else if (tokenRoles.includes('billing')) roleDisplay = '(Billing)';
    else roleDisplay = '(User)';
    
    console.log('✅ [getUserDisplayName] Legacy currentUserInfo match:', `${currentUserInfo.fullName} ${roleDisplay}`);
    return `${currentUserInfo.fullName} ${roleDisplay}`;
  }
  
  // CONTACTS: Try to find user in contacts (for other users/clients)  
  const contact = contacts.find(c => 
    c.id === userId || 
    c.assigned_user_id === userId ||
    userId.includes(c.id.slice(0, 8)) ||
    c.id.includes(userId.slice(0, 8))
  );
  
  if (contact) {
    const roleContext = contact.contact_type === 'individual' 
      ? (contact.status === 'converted' ? '(Client)' : '(Contact)')
      : '(Organization)';
    console.log('✅ [getUserDisplayName] Contact match:', `${contact.name} ${roleContext}`);
    return `${contact.name} ${roleContext}`;
  }
  
  // SYSTEM: Check if it's a system user (auto-save)
  if (userId.includes('system') || userId.includes('auto')) {
    console.log('✅ [getUserDisplayName] System user detected');
    return 'System (Auto-save)';
  }
  
  // SMART FALLBACK: For unknown UUIDs, show as "Other User" with current user context
  if (tokenUserName) {
    console.log('⚠️ [getUserDisplayName] Unknown user in current session');
    return `Other User (${userId.slice(0, 8)}...)`;
  }
  
  // BASIC FALLBACK: No context available
  console.log('⚠️ [getUserDisplayName] No user context available');
  return `User (${userId.slice(0, 8)}...)`;
};

export const getVersionStats = (
  version: DocumentVersion, 
  isCurrentVersion: boolean, 
  currentDocumentStats?: { wordCount: number; characterCount: number; pageCount: number }
): VersionStats => {
  // Enhancement 3: REAL data from props + database fallback
  const sizeKB = Math.round((version.size_bytes || 0) / 1024);
  
  // USE REAL DATA: If this is the current version and we have real stats
  if (isCurrentVersion && currentDocumentStats) {
    console.log('✅ [getVersionStats] Using REAL data from props:', {
      realWords: currentDocumentStats.wordCount,
      realPages: currentDocumentStats.pageCount,
      realChars: currentDocumentStats.characterCount
    });
    
    const sizeDisplay = sizeKB > 1024 
      ? `${(sizeKB / 1024).toFixed(1)} MB` 
      : `${sizeKB} KB`;
    
    return {
      words: currentDocumentStats.wordCount || 0,
      pages: currentDocumentStats.pageCount || 1,
      sizeDisplay
    };
  }
  
  // FALLBACK: Smart file size estimation for non-current versions
  console.log('⚠️ [getVersionStats] Using size estimation for historical version', {
    versionId: version.version_id,
    sizeBytes: version.size_bytes,
    sizeKB: sizeKB,
    mimeType: version.mime_type
  });
  
  // Use REAL file size and MIME type for better word count estimation
  let wordsPerKB = 120; // Default for text content
  
  // Adjust estimation based on actual MIME type from database
  if (version.mime_type) {
    if (version.mime_type.includes('json')) {
      wordsPerKB = 80; // JSON has more structure, fewer actual words
    } else if (version.mime_type.includes('pdf')) {
      wordsPerKB = 200; // PDFs are more word-dense
    } else if (version.mime_type.includes('docx')) {
      wordsPerKB = 150; // Word docs are efficient
    }
  }
  
  // Calculate estimated words from REAL file size
  const estimatedWords = sizeKB > 0 ? Math.round(sizeKB * wordsPerKB) : 0;
  
  // Use legal standard: 250 words per page
  const estimatedPages = Math.max(1, Math.ceil(estimatedWords / 250));
  
  // REAL file size display from database
  const sizeDisplay = sizeKB > 1024 
    ? `${(sizeKB / 1024).toFixed(1)} MB` 
    : `${sizeKB} KB`;
    
  return {
    words: estimatedWords,
    pages: estimatedPages,
    sizeDisplay
  };
};

export const getVersionStatusInfo = (
  version: DocumentVersion, 
  isCurrentVersion: boolean, 
  isLatestVersion: boolean,
  currentVersionId?: string
): VersionStatusInfo => {
  // Enhancement 4: REAL status from database fields
  
  // Check if this version is promoted (real database field)
  if (version.is_promoted) {
    return {
      status: 'promoted',
      color: 'purple',
      icon: '🟣',
      badge: 'Promoted',
      description: `Promoted on ${new Date(version.promoted_at || '').toLocaleDateString()}`
    };
  }
  
  // Check if currently active
  if (isCurrentVersion || version.version_id === currentVersionId) {
    return {
      status: 'active',
      color: 'green',
      icon: '🟢',
      badge: 'Active',
      description: 'Currently active version'
    };
  }
  
  // Check if latest but not active
  if (isLatestVersion) {
    return {
      status: 'latest',
      color: 'blue', 
      icon: '🔵',
      badge: 'Latest',
      description: 'Most recent version'
    };
  }
  
  // Check if checked in (real database field)
  if (version.checked_in_by_user_id && version.checked_in_at) {
    return {
      status: 'checked_in',
      color: 'green',
      icon: '✅',
      badge: 'Checked In',
      description: `Checked in on ${new Date(version.checked_in_at).toLocaleDateString()}`
    };
  }
  
  // Determine status based on real timestamps
  const daysOld = Math.floor((Date.now() - new Date(version.created_at).getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysOld > 30) {
    return {
      status: 'archived',
      color: 'gray',
      icon: '📁',
      badge: 'Archived',
      description: `${daysOld} days old`
    };
  }
  
  return {
    status: 'draft',
    color: 'yellow',
    icon: '📝', 
    badge: 'Draft',
    description: 'Previous version'
  };
};

export const getRelativeTime = (dateString: string): string => {
  // Enhancement 3: REAL relative timestamps from database dates
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const groupVersionsByDate = (versions: DocumentVersion[]): Record<string, DocumentVersion[]> => {
  // Enhancement 5: REAL date grouping from database timestamps
  const groups: Record<string, DocumentVersion[]> = {};
  const now = new Date();
  
  versions.forEach(version => {
    // Use actual created_at timestamp from database
    const versionDate = new Date(version.created_at);
    const diffDays = Math.floor((now.getTime() - versionDate.getTime()) / (1000 * 60 * 60 * 24));
    
    let groupKey: string;
    if (diffDays === 0) {
      groupKey = '📅 Today';
    } else if (diffDays === 1) {
      groupKey = '📅 Yesterday';
    } else if (diffDays < 7) {
      groupKey = '📅 This Week';
    } else if (diffDays < 30) {
      groupKey = '📅 This Month';
    } else if (diffDays < 365) {
      groupKey = '📅 Earlier This Year';
    } else {
      groupKey = '📅 Previous Years';
    }
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(version);
  });
  
  return groups;
};
